import React, { useContext, useEffect, useState } from "react";
import { Package } from "lucide-react";
import { CrudActionsMenu } from "../../../components/admin/CrudActionsMenu";
import { usePagedRows, useClampPage } from "../../../hooks/usePagedRows";
import {
  Table,
  Button,
  Modal,
  notification,
  Form,
  Select,
  Pagination,
  Input,
  message,
  PageHeader,
  PageStack,
} from "lib/ui";
import { PlusOutlined, DeleteOutlined } from "lib/icons";
import AddCouponForm from "./Add"; // Adjust the path as necessary
import fetchApi from "../../../hooks/ApiFetch";
import { AuthContext } from "../../../context/Context";
import ApiPath from "../../../hooks/ApiFetch";

const { Option } = Select;

function isHttpUrl(s) {
  return /^https?:\/\//i.test(String(s ?? "").trim());
}

function promoCodeFromCoupon(c) {
  const raw = String(c?.code ?? "").trim();
  if (isHttpUrl(raw)) {
    const meta = Array.isArray(c?.metaFields)
      ? c.metaFields.find((m) => m?.key === "promoCode")
      : null;
    const v = meta?.value != null ? String(meta.value).trim() : "";
    return v || "—";
  }
  return raw || "—";
}

function bannerUrlFromCoupon(c) {
  const img = String(c?.image ?? "").trim();
  if (img) return img;
  const raw = String(c?.code ?? "").trim();
  return isHttpUrl(raw) ? raw : "";
}

const App = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(undefined); // Track the coupon being edited
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchCode, setSearchCode] = useState(""); // Search term for coupon code
  const [searchType, setSearchType] = useState(""); // Search term for coupon type
  const { coupons: data, setcoupons: setData } = useContext(AuthContext);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetchApi.get(`/coupons`);
      setData(response.data);
    } catch (error) {
      message.error("Failed to fetch data.");
    }
  };

  const handleAddCoupon = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ status: true });
    setUploadedFile("");
    setIsModalVisible(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingUser(coupon);
    setUploadedFile(bannerUrlFromCoupon(coupon));
    let codeField = String(coupon?.code ?? "").trim();
    if (isHttpUrl(codeField)) {
      const meta = Array.isArray(coupon?.metaFields)
        ? coupon.metaFields.find((m) => m?.key === "promoCode")
        : null;
      codeField = meta?.value != null ? String(meta.value).trim() : "";
    }
    form.setFieldsValue({
      title: coupon.title,
      detail: coupon.detail,
      code: codeField,
      type: coupon.type,
      DiscountType: coupon.DiscountType,
      Unit: coupon.Unit,
      status: coupon.status !== false,
    });
    setIsModalVisible(true);
  };

  const handleDeleteCoupon = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this coupon?",
      icon: <DeleteOutlined />,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          await ApiPath.delete(`/coupons/${id}`);
          setData((prevData) => prevData.filter((item) => item._id !== id));
          notification.success({
            message: "Coupon Deleted",
            description: "Coupon has been deleted successfully.",
          });
        } catch (error) {
          notification.error({
            message: "Operation Failed",
            description: "An error occurred while deleting the coupon.",
          });
        }
      },
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setUploadedFile("");
  };

  const handleOk = async (values) => {
    try {
      const { _id, __v, createdAt, updatedAt, metaFields, ...rest } = values;
      const body = {
        ...rest,
        code: String(rest.code ?? "").trim(),
        image: (uploadedFile ?? "").trim(),
      };

      if (editingUser) {
        const res = await ApiPath.put(`/coupons/${editingUser._id}`, body);
        setData((prevData) =>
          prevData.map((item) =>
            item._id === editingUser._id ? { ...item, ...res.data } : item
          )
        );
        notification.success({
          message: "Coupon Updated",
          description: "Coupon has been updated successfully.",
        });
      } else {
        const response = await ApiPath.post(`/coupons`, body);
        setData((prevData) => [...prevData, response.data]);
        notification.success({
          message: "Coupon Added",
          description: "Coupon has been added successfully.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
      setUploadedFile("");
    } catch (error) {
      notification.error({
        message: "Operation Failed",
        description: "An error occurred while processing your request.",
      });
      console.log("Error", error);
    }
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const q = searchCode.toLowerCase();
    const promo = promoCodeFromCoupon(item).toLowerCase();
    const banner = bannerUrlFromCoupon(item).toLowerCase();
    const type = (item.type ?? "").toString().toLowerCase();
    return (
      (promo.includes(q) || banner.includes(q)) &&
      type.includes(searchType.toLowerCase())
    );
  });

  useClampPage(filteredData.length, currentPage, pageSize, setCurrentPage);
  const pagedData = usePagedRows(filteredData, currentPage, pageSize);

  const columns = [
    {
      title: "#",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: (
        <div>
          Code
          <Input
            placeholder="Search code or banner URL"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            className="mt-2 max-w-[220px]"
          />
        </div>
      ),
      key: "code",
      render: (_, record) => (
        <span className="font-mono text-sm text-slate-900">
          {promoCodeFromCoupon(record)}
        </span>
      ),
    },
    {
      title: "Banner",
      key: "image",
      render: (_, record) => {
        const u = bannerUrlFromCoupon(record);
        return u ? (
          <span
            className="block max-w-[220px] truncate text-xs text-slate-500"
            title={u}
          >
            {u}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        );
      },
    },
    {
      title: (
        <div>
          Type
          <Input
            placeholder="Search Type"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="mt-2 max-w-[220px]"
          />
        </div>
      ),
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Details",
      dataIndex: "detail",
      key: "detail",
      render: (text) => (
        <span className="text-slate-700">{text ?? "—"}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 52,
      align: "right",
      render: (_, record) => (
        <CrudActionsMenu
          ariaLabel={`Coupon ${record.title ?? promoCodeFromCoupon(record)}`}
          onEdit={() => handleEditCoupon(record)}
          onDelete={() => handleDeleteCoupon(record._id)}
        />
      ),
    },
  ];

  return (
    <PageStack>
      <PageHeader
        title="Coupons"
        description="Create and manage discount codes and promotions."
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCoupon}
          >
            Add Coupon
          </Button>
        }
      />
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Package className="h-4 w-4 shrink-0 text-brand" />
          <span>
            <span className="font-semibold text-slate-800">
              {filteredData.length}
            </span>{" "}
            coupons
          </span>
        </div>
        <Table
          columns={columns}
          dataSource={pagedData}
          rowKey="_id"
          pagination={false}
          footer={() => (
            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span className="text-slate-500">Rows per page</span>
                <Select
                  value={pageSize}
                  style={{ width: 100 }}
                  onChange={handlePageSizeChange}
                >
                  <Option value={10}>10</Option>
                  <Option value={20}>20</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                </Select>
              </div>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total}`
                }
                total={filteredData.length}
              />
            </div>
          )}
        />
      </div>
      <Modal
        title={editingUser ? "Edit coupon" : "New coupon"}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={720}
        className="!max-w-[min(96vw,720px)]"
      >
        <AddCouponForm
          form={form}
          onFinish={handleOk}
          initialValues={editingUser || {}}
          edit={!!editingUser}
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
        />
      </Modal>
    </PageStack>
  );
};

export default App;
